import { SxProps, Theme } from '@mui/material';

/**
 * Единая иерархия «подпись → значение» для карточек и блоков просмотра.
 *
 * Подпись — серая (`text.secondary`), значение — основным цветом
 * (`text.primary`), обе Roboto 14/400: как в новой анкете актёра (19453).
 * Индиговая подпись читалась как ссылка, хотя не кликается; индиго теперь
 * только у ссылок. Иерархию держит цвет: значение — то, ради чего смотрят
 * поле, поэтому оно контрастнее подписи.
 *
 * Кегль подписи равен кеглю значения (14px): подписи вроде «Количество
 * активных кастингов» на 16px переносились в три строки и ломали ряды карточек.
 *
 * Контракт остаётся механизмом: консьюмеры не хардкодят цвет и семейство, а
 * берут эти объекты. Меняются только значения внутри.
 */
export const FIELD_LABEL_SX: SxProps<Theme> = {
  color: 'text.secondary',
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.43
};

/**
 * Значение поля: Roboto 14/400 основным цветом.
 * Контраст с подписью держит цвет (основной против приглушённого).
 */
export const FIELD_VALUE_SX: SxProps<Theme> = {
  color: 'text.primary',
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.43
};

/**
 * Вторичное значение: дата, год, мета, роль под именем — то, что стоит рядом
 * с заголовком или значением и не должно с ним спорить. Тот же Roboto 14/400,
 * но приглушённым цветом (`text.secondary`), как подпись (19453).
 */
export const FIELD_SECONDARY_SX: SxProps<Theme> = {
  ...FIELD_VALUE_SX,
  color: 'text.secondary'
};

/**
 * Плотная пара для карточек списков (проекты, кастинги, футер карточки).
 *
 * Там подпись и значение стоят в одну строку или в ячейке 44px высотой, и
 * базовые 16/14 ломали ряд: подпись «Количество активных кастингов» переносилась
 * в три строки, а инлайновое «Место съемки: …» распадалось на два кегля. Кегль
 * 12px — ровно тот, что стоял в этих карточках раньше; иерархию здесь несёт
 * цвет (серая подпись, значение основным цветом), а не размер.
 */
export const FIELD_LABEL_DENSE_SX: SxProps<Theme> = {
  ...FIELD_LABEL_SX,
  fontSize: '12px',
  // Высота строки — как у плотного значения: пара стоит в одну строку с
  // `alignItems: center`, и при разной высоте строки текст значения уезжал
  // вверх относительно подписи («Режиссёр: Не указан» на плитке проекта)
  lineHeight: 1.33
};

/**
 * Значение поля в плотной карточке — тот же основной цвет, кегль 12px.
 */
export const FIELD_VALUE_DENSE_SX: SxProps<Theme> = {
  ...FIELD_VALUE_SX,
  fontSize: '12px',
  lineHeight: 1.33
};

/**
 * Вторичное значение в плотной карточке — приглушённый цвет, кегль 12px.
 */
export const FIELD_SECONDARY_DENSE_SX: SxProps<Theme> = {
  ...FIELD_VALUE_DENSE_SX,
  color: 'text.secondary'
};

/**
 * Подпись поля в секциях просмотра (детали проекта, детали кастинга).
 *
 * Подпись крупнее значения (16/500 против 14/400) и серая, как подпись поля
 * (19453): значение — основным цветом, подпись приглушённая. Подписи в секциях
 * короткие («Тип проекта», «Описание проекта»), рядов карточек они не ломают,
 * поэтому 16px здесь безопасны — в отличие от плотных карточек.
 */
export const SECTION_LABEL_SX: SxProps<Theme> = {
  color: 'text.secondary',
  fontFamily: 'Montserrat Variable, Helvetica, Arial, sans-serif',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: 1.5
};
